/**
 * The two things every surface that lists conversations needs from the running chat: the list
 * kept in step with the turn in flight, and the id of the conversation currently on screen.
 *
 * Both used to live inside `ChatHistorySidebar`, which was the only such surface. The unified
 * shell has a second one — the investor rail's Chats group — and there the sidebar is not
 * mounted at all, so a hook is what lets either surface own the job without the other being on
 * screen (PLT-6610). Exactly one of the two renders at a time: the sidebar on `/chat` outside
 * the unified shell, the rail inside it.
 */
import { useAssistantRuntime } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { composerState } from '../upright-chat-shared';
import {
  type ChatConversationListItem,
  refreshConversationListForGeneratedTitle,
  refreshConversationListUntilPersisted,
  upsertOptimisticConversation,
} from './chat-history-data';
import { getChatHistoryState, setActiveConversationId, subscribeChatHistoryState } from './chat-history-state';

/**
 * Track the chat's running state on every notify. Two transitions matter for a conversation
 * list:
 *
 *   - false → true: a turn has just started. The user has clicked Send. The server will only
 *     persist the conversation row after the turn finishes, so without an optimistic insert
 *     here the row would only appear after the assistant has finished responding — which gives
 *     the user the impression that nothing was saved yet. PLT-4861 #4. We reach into the
 *     thread's messages to pull the user's first text and synthesise a placeholder row keyed on
 *     the current `composerState.threadId` (== the conversation id).
 *
 *   - true → false: a turn has just finished. The canonical row is written by the
 *     *fire-and-forget* `persistChatTurn`, which commits a few hundred ms after the stream
 *     ends, so we can't just refetch once — a single eager refetch races ahead of the write and
 *     gets a list without the new row. Instead we refresh with a bounded retry until the server
 *     returns it, at which point the optimistic row is replaced by the server row (same id)
 *     with the real title / updatedAt / etc. (PLT-5287).
 *
 * We don't subscribe to message-level events because (a) the persistence write is
 * fire-and-forget and (b) one optimistic insert per turn is enough — re-running on every
 * streamed token would just thrash the cache.
 */
export function useChatConversationListSync(): void {
  const runtime = useAssistantRuntime();
  const queryClient = useQueryClient();

  useEffect(() => {
    let wasRunning = false;
    const unsubscribe = runtime.thread.subscribe(() => {
      const isRunning = runtime.thread.getState().isRunning;
      if (!wasRunning && isRunning) {
        // Turn just started — surface the row optimistically.
        const messages = runtime.thread.getState().messages;
        const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
        const userText = lastUserMessage ? extractUserMessageText(lastUserMessage.content) : null;
        const nowIso = new Date().toISOString();
        const optimisticRow: ChatConversationListItem = {
          id: composerState.threadId,
          title: null,
          titleIsAutogen: true,
          pageContextHref: null,
          createdAt: nowIso,
          updatedAt: nowIso,
          firstUserMessageSnippet: userText !== null ? truncateForSnippet(userText) : null,
        };
        upsertOptimisticConversation(queryClient, optimisticRow);
        setActiveConversationId(composerState.threadId);
      }
      if (wasRunning && !isRunning) {
        // Turn just finished — refresh until the server-written row lands, then the optimistic
        // row is replaced by the canonical row (same id, enriched with a real title / updated
        // `updatedAt`).
        setActiveConversationId(composerState.threadId);
        void refreshConversationListUntilPersisted(queryClient, composerState.threadId).then(async () => {
          // Then once more, a beat later, for the generated title (PLT-4866). The server writes
          // it behind the persist, after the response stream has closed, so the refresh above is
          // guaranteed to be too early for it. Cheap and self-cancelling: it does nothing for a
          // conversation that already has a title.
          await refreshConversationListForGeneratedTitle(queryClient, composerState.threadId);
        });
      }
      wasRunning = isRunning;
    });
    return unsubscribe;
  }, [runtime, queryClient]);
}

/**
 * The conversation currently on screen, or `null` on a fresh `New chat` with nothing sent yet.
 *
 * Read it rather than the pathname when marking a row as the current one. The id moves without
 * a navigation — a first turn sent from an empty thread becomes a conversation where the user
 * already stands — so a surface that derived the highlight from the URL would leave a new
 * conversation unmarked in its own list until the user navigated somewhere.
 */
export function useActiveConversationId(): string | null {
  return useSyncExternalStore(
    subscribeChatHistoryState,
    () => getChatHistoryState().activeConversationId,
    () => getChatHistoryState().activeConversationId,
  );
}

// ---------------------------------------------------------------------------
// Helpers for the optimistic-insert path (PLT-4861 #4).
//
// The shape of `ThreadMessage.content` is the assistant-ui union: either a plain string or an
// array of structured parts (text, image, tool-call, …). We only care about user-typed text, so
// we walk the array and pull out `text` parts. Anything we don't recognise (image attachments,
// hidden context blocks) is ignored — the snippet is purely cosmetic, so dropping non-text
// parts is the right behaviour.
// ---------------------------------------------------------------------------

/** Maximum characters in the optimistic snippet — same cap the server uses. */
const MAX_OPTIMISTIC_SNIPPET_LENGTH = 80;

function extractUserMessageText(content: unknown): string | null {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return null;
  const parts: string[] = [];
  for (const part of content) {
    if (
      part !== null &&
      typeof part === 'object' &&
      'type' in part &&
      (part as { type: unknown }).type === 'text' &&
      'text' in part &&
      typeof (part as { text: unknown }).text === 'string'
    ) {
      parts.push((part as { text: string }).text);
    }
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Mirrors the server's `truncateForSnippet` — see
 * `services/main/upserve/modules/chat/conversations.ts`. We don't share the implementation
 * across the build boundary because the backend helper isn't exported, but the cap (80 chars)
 * and the trailing single-character ellipsis match. When the server-written row eventually
 * replaces this optimistic one, the snippet may shift by a few characters at the boundary —
 * close enough not to flicker.
 */
function truncateForSnippet(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= MAX_OPTIMISTIC_SNIPPET_LENGTH) return trimmed;
  const cut = trimmed.slice(0, MAX_OPTIMISTIC_SNIPPET_LENGTH);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > MAX_OPTIMISTIC_SNIPPET_LENGTH * 0.6 ? cut.slice(0, lastSpace) : cut;
  return base + '…';
}
